import React, { Fragment, useRef, useState } from 'react';
import {
    Button,
    useNotify,
    useDataProvider,
    useGetList,
    SimpleShowLayout,
    ArrayField,
    Datagrid,
    TextField,
    FunctionField,
    useRecordContext,
} from 'react-admin';
import IconContentAdd from '@mui/icons-material/GetApp';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFileOutlined';
import CloudUploadIcon from '@mui/icons-material/CloudUploadOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import {
    Box,
    CircularProgress,
    Typography,
    Alert,
    AlertTitle,
    List as MuiList,
    ListItem,
    ListItemText,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { isValidCPF } from '@brazilian-utils/brazilian-utils';
import { formatCpf, getChainId } from '../lib';
import Permission from './Permission';
import { PERMISSIONS } from '../constants';
import { onlyNumbers } from '../utils';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const COLUMN_TOKENS = {
    cpf: ['cpf'],
    nome: ['nome', 'name'],
    identfid: ['ident', 'matricul', 'codigo'],
};

const REQUIRED_COLUMNS = [
    { key: 'cpf', label: 'cpf', description: 'CPF do frentista' },
    { key: 'nome', label: 'nome', description: 'Nome completo' },
    { key: 'identfid', label: 'identificacao', description: 'Código do frentista' },
];

const columnLabel = (key) => REQUIRED_COLUMNS.find(c => c.key === key)?.label || key;

const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
        ['cpf', 'nome', 'identificacao'],
        ['12345678901', 'João da Silva', '001'],
    ]);
    // Marca as células de CPF e identificação como texto para preservar zeros à esquerda
    ['A2', 'C2'].forEach((addr) => {
        if (ws[addr]) {
            ws[addr].t = 's';
            ws[addr].z = '@';
        }
    });
    ws['!cols'] = [{ wch: 14 }, { wch: 30 }, { wch: 16 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Frentistas');
    XLSX.writeFile(wb, 'modelo-frentistas.xlsx');
};

const normalizeKey = (value) =>
    String(value ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .trim()
        .toLowerCase();

const findColumnKey = (sampleRow, tokens) => {
    const keys = Object.keys(sampleRow);
    return keys.find(k => {
        const normalized = normalizeKey(k);
        if (!normalized) return false;
        return tokens.some(token => normalized.includes(token));
    }) || null;
};

const parseCsv = (file) =>
    new Promise((resolve, reject) => {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (result) => {
                if (result.errors && result.errors.length) {
                    reject(new Error(`Erro ao ler o CSV: ${result.errors[0].message}`));
                    return;
                }
                resolve(result.data);
            },
            error: (err) => reject(new Error(`Erro ao ler o CSV: ${err.message}`)),
        });
    });

const parseXlsx = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const workbook = XLSX.read(e.target.result, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                if (!sheetName) {
                    reject(new Error('A planilha está vazia.'));
                    return;
                }
                const sheet = workbook.Sheets[sheetName];
                resolve(XLSX.utils.sheet_to_json(sheet, { defval: '' }));
            } catch (err) {
                reject(new Error(`Erro ao ler o arquivo Excel: ${err.message}`));
            }
        };
        reader.onerror = () => reject(new Error('Não foi possível ler o arquivo.'));
        reader.readAsArrayBuffer(file);
    });

const parseSpreadsheet = async (file) => {
    const fileName = (file?.name || '').toLowerCase();
    if (fileName.endsWith('.csv')) return parseCsv(file);
    if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) return parseXlsx(file);
    throw new Error('Formato não suportado. Use um arquivo .csv, .xlsx ou .xls.');
};

const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ColumnChip = ({ name, description, error, valid }) => {
    const tone = error ? 'error' : valid ? 'success' : 'neutral';
    const accentColor = tone === 'error' ? 'error.main' : tone === 'success' ? 'success.main' : null;

    return (
        <Box
            sx={(theme) => ({
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                border: '1px solid',
                borderColor: tone === 'error'
                    ? 'error.main'
                    : tone === 'success'
                        ? 'success.main'
                        : 'divider',
                borderRadius: 999,
                px: 1.5,
                py: 0.75,
                bgcolor: tone === 'error'
                    ? alpha(theme.palette.error.main, 0.08)
                    : tone === 'success'
                        ? alpha(theme.palette.success.main, 0.08)
                        : 'background.paper',
            })}
        >
            {tone === 'error' && <ErrorOutlineIcon sx={{ fontSize: 18, color: 'error.main' }} />}
            {tone === 'success' && <CheckCircleOutlineIcon sx={{ fontSize: 18, color: 'success.main' }} />}
            <Typography
                component="span"
                sx={{
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    color: accentColor || 'text.primary',
                }}
            >
                {name}
            </Typography>
            <Typography component="span" variant="body2" color={accentColor || 'text.secondary'}>
                · {description}
            </Typography>
        </Box>
    );
};

const TemplateCard = () => (
    <Box
        onClick={downloadTemplate}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                downloadTemplate();
            }
        }}
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            cursor: 'pointer',
            transition: 'background-color .15s, border-color .15s',
            '&:hover': { borderColor: 'success.main', bgcolor: 'action.hover' },
        }}
    >
        <Box
            sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                bgcolor: '#e8f5e9',
                color: 'success.dark',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 1,
            }}
        >
            <FileDownloadIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" fontWeight={600}>
                Baixar planilha modelo
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Já vem com as colunas certas e um exemplo preenchido
            </Typography>
        </Box>
        <ChevronRightIcon color="action" />
    </Box>
);

const validateFile = async (file) => {
    if (file.size > MAX_FILE_SIZE) {
        return {
            status: 'invalid',
            fileError: `O arquivo tem ${formatBytes(file.size)} e o limite é 5 MB.`,
            missingColumns: [],
            rowErrors: [],
            validRows: [],
        };
    }

    let rows;
    try {
        rows = await parseSpreadsheet(file);
    } catch (err) {
        return { status: 'invalid', fileError: err.message, missingColumns: [], rowErrors: [], validRows: [] };
    }

    if (!rows || !rows.length) {
        return {
            status: 'invalid',
            fileError: 'O arquivo está vazio. Verifique se há linhas além do cabeçalho.',
            missingColumns: [],
            rowErrors: [],
            validRows: [],
        };
    }

    const sample = rows[0];
    const columnMap = {
        cpf: findColumnKey(sample, COLUMN_TOKENS.cpf),
        nome: findColumnKey(sample, COLUMN_TOKENS.nome),
        identfid: findColumnKey(sample, COLUMN_TOKENS.identfid),
    };
    const missingColumns = Object.entries(columnMap).filter(([, key]) => !key).map(([col]) => col);
    if (missingColumns.length) {
        return { status: 'invalid', fileError: null, missingColumns, rowErrors: [], validRows: [] };
    }

    const rowErrors = [];
    const validRows = [];
    rows.forEach((row, index) => {
        const lineNumber = index + 2;
        const rawCpf = row[columnMap.cpf];
        const rawName = row[columnMap.nome];
        const rawIdentfid = row[columnMap.identfid];

        const cpfDigits = onlyNumbers(rawCpf);
        // Excel/XLSX salva CPF como número e perde zeros à esquerda — restaura padding até 11 dígitos
        const cpf = cpfDigits && cpfDigits.length < 11 ? cpfDigits.padStart(11, '0') : cpfDigits;
        const name = String(rawName ?? '').trim();
        const identfid = String(rawIdentfid ?? '').trim();

        if (!cpf && !name && !identfid) return;

        const reasons = [];
        if (!cpf) reasons.push('CPF vazio');
        else if (!isValidCPF(cpf)) reasons.push(`CPF inválido (${rawCpf})`);
        if (!name) reasons.push('Nome vazio');
        if (!identfid) reasons.push('Identificação vazia');

        if (reasons.length) {
            rowErrors.push({ line: lineNumber, cpf: rawCpf, name: rawName, identfid: rawIdentfid, reasons });
            return;
        }

        validRows.push({ name, cpf, identfid });
    });

    if (!validRows.length) {
        return {
            status: 'invalid',
            fileError: 'Nenhuma linha válida encontrada no arquivo.',
            missingColumns: [],
            rowErrors,
            validRows: [],
        };
    }

    return { status: 'valid', fileError: null, missingColumns: [], rowErrors, validRows };
};

const FileDropZone = ({ file, onFile, status, disabled }) => {
    const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
        multiple: false,
        disabled,
        accept: {
            'text/csv': ['.csv'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'application/vnd.ms-excel': ['.xls'],
        },
        onDrop: (accepted) => {
            if (accepted && accepted[0]) onFile(accepted[0]);
        },
    });

    const isError = status === 'invalid';
    const isValid = status === 'valid';

    const borderColor = isDragReject || isError
        ? 'error.main'
        : isValid
            ? 'success.main'
            : isDragActive
                ? 'primary.main'
                : 'divider';

    return (
        <Box
            {...getRootProps()}
            sx={(theme) => ({
                mt: 2,
                py: 5,
                px: 2,
                textAlign: 'center',
                border: '2px dashed',
                borderColor,
                borderRadius: 2,
                bgcolor: isError
                    ? alpha(theme.palette.error.main, 0.04)
                    : isValid
                        ? alpha(theme.palette.success.main, 0.04)
                        : isDragActive
                            ? 'action.hover'
                            : 'background.default',
                cursor: disabled ? 'not-allowed' : 'pointer',
                transition: 'border-color .15s, background-color .15s',
                opacity: disabled ? 0.7 : 1,
            })}
        >
            <input {...getInputProps()} />
            {file ? (
                <>
                    <InsertDriveFileIcon
                        sx={{ fontSize: 32, color: isError ? 'error.main' : 'success.main', mb: 1 }}
                    />
                    <Typography variant="body1" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                        {file.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Clique para escolher outro arquivo
                    </Typography>
                </>
            ) : (
                <>
                    <UploadFileIcon sx={{ fontSize: 32, color: 'action.active', mb: 1 }} />
                    <Typography variant="body1" fontWeight={600}>
                        Arraste e solte ou clique para selecionar
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        .csv, .xlsx ou .xls · até 5 MB
                    </Typography>
                </>
            )}
        </Box>
    );
};

const ChainSelect = ({ value, onChange }) => {
    const { data, isLoading } = useGetList(
        'chains',
        { pagination: { page: 1, perPage: null }, sort: { field: 'name', order: 'ASC' } },
    );

    return (
        <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel id="import-chain-select-label">Rede</InputLabel>
            <Select
                labelId="import-chain-select-label"
                label="Rede"
                value={value || ''}
                disabled={isLoading}
                onChange={(e) => onChange(e.target.value)}
            >
                {(data || []).map((chain) => (
                    <MenuItem key={chain.id} value={chain.id}>
                        {chain.name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

const ConflictView = ({ conflictAttendants, xmlConflictAttendants, chainId, onResolveAll }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();

    const getAttendantsWithConflicts = () => conflictAttendants.map(attendant => ({
        ...attendant,
        newAttendant: xmlConflictAttendants.find(x => x.identfid === attendant.identfid),
    }));

    const ActionConflictButtons = () => {
        const [isDisabled, setIsDisabled] = useState(false);
        const postCommit = () => setIsDisabled(true);
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <KeepButton isDisabled={isDisabled} postCommit={postCommit} />
                <IgnoreButton isDisabled={isDisabled} postCommit={postCommit} />
            </div>
        );
    };

    const KeepButton = ({ isDisabled, postCommit }) => {
        const record = useRecordContext();
        const [isLoading, setIsLoading] = useState(false);
        const [checked, setChecked] = useState(false);
        const { newAttendant, ...attendant } = record;

        const updateRequest = () => {
            setIsLoading(true);
            dataProvider.update('attendants', { id: attendant.id, data: { ...newAttendant, chainId }, previousData: attendant })
                .then(() => {
                    setChecked(true);
                    postCommit && postCommit();
                    notify('Registro atualizado com sucesso', { messageArgs: { smart_count: 1 } });
                })
                .catch(() => notify('Não foi possível atualizar o registro'))
                .finally(() => setIsLoading(false));
        };

        return (
            <Button type="button" startIcon={isLoading ? <CircularProgress size={16} /> : <></>} disabled={isLoading || isDisabled} label="Substituir pela planilha" variant={checked ? 'contained' : 'outlined'} onClick={updateRequest} />
        );
    };

    const IgnoreButton = ({ isDisabled, postCommit }) => {
        const [checked, setChecked] = useState(false);
        const ignoreItem = () => {
            notify('Registro mantido sem alteração');
            setChecked(true);
            postCommit && postCommit();
        };
        return <Button type="button" disabled={isDisabled} label="Manter atual" variant={checked ? 'contained' : 'outlined'} onClick={ignoreItem} />;
    };

    return (
        <Box width="100%">
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography variant="body1">Os registros abaixo já existem no banco com CPF diferente. Escolha linha a linha ou resolva todos de uma vez.</Typography>
                    <Typography variant="body2" color="error">{conflictAttendants.length} conflito(s) encontrado(s)</Typography>
                </Box>
                <Button type="button" label="Substituir todos pela planilha" variant="contained" onClick={onResolveAll} />
            </Box>
            <SimpleShowLayout sx={{ width: '100%', padding: 0, marginTop: 2 }} record={{ attendants: getAttendantsWithConflicts() }}>
                <ArrayField source="attendants" label="Frentistas">
                    <Datagrid bulkActionButtons={false} rowClick={false}>
                        <TextField source="identfid" label="Identfid" />
                        <FunctionField label="Nome" render={record => (
                            <>
                                <Typography variant="body1">Atual: {record.name}</Typography>
                                {record.newAttendant ? <Typography variant="body2" color="textSecondary">Planilha: {record.newAttendant.name}</Typography> : <></>}
                            </>
                        )} />
                        <FunctionField label="CPF" render={record => (
                            <>
                                <Typography variant="body1">Atual: {formatCpf(record.cpf)}</Typography>
                                {record.newAttendant ? <Typography variant="body2" color="textSecondary">Planilha: {formatCpf(record.newAttendant.cpf)}</Typography> : <></>}
                            </>
                        )} />
                        <ActionConflictButtons label="Ação" />
                    </Datagrid>
                </ArrayField>
            </SimpleShowLayout>
        </Box>
    );
};

const ImportDialog = ({ open, onClose }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();

    const [chainId, setChainId] = useState(getChainId());
    const [selectedFile, setSelectedFile] = useState(null);
    const [validation, setValidation] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [conflictAttendants, setConflictAttendants] = useState([]);
    const [xmlConflictAttendants, setXmlConflictAttendants] = useState([]);
    const [showConflictList, setShowConflictList] = useState(false);
    const validationIdRef = useRef(0);

    const resetState = () => {
        setSelectedFile(null);
        setValidation(null);
        setIsLoading(false);
        setConflictAttendants([]);
        setXmlConflictAttendants([]);
        setShowConflictList(false);
    };

    const handleClose = () => {
        if (isLoading) return;
        resetState();
        onClose();
    };

    const onSelectFile = async (file) => {
        const id = ++validationIdRef.current;
        setSelectedFile(file);
        setValidation({ status: 'validating', fileError: null, missingColumns: [], rowErrors: [], validRows: [] });
        const result = await validateFile(file);
        if (validationIdRef.current === id) {
            setValidation(result);
        }
    };

    const findAttendantsApi = (filter, xmlAttendants) => {
        setIsLoading(true);
        dataProvider.getList('attendants', { filter, pagination: { perPage: null, page: 1 } })
            .then(({ data }) => {
                const withConflicts = data.filter(item => {
                    const xmlAttendant = xmlAttendants.find(x => String(x.identfid) === String(item.identfid));
                    return xmlAttendant && item.cpf && String(xmlAttendant.cpf) !== String(item.cpf);
                });

                const withAutoUpdate = data.filter(item => {
                    const xmlAttendant = xmlAttendants.find(x => String(x.identfid) === String(item.identfid));
                    if (!xmlAttendant) return false;
                    if (item.cpf && String(xmlAttendant.cpf) !== String(item.cpf)) return false;
                    return (!item.cpf && xmlAttendant.cpf) || (!item.name && xmlAttendant.name);
                });

                const withoutConflicts = xmlAttendants.filter(x => !data.find(item => String(item.identfid) === String(x.identfid)));

                withAutoUpdate.forEach(item => {
                    const xmlAttendant = xmlAttendants.find(x => String(x.identfid) === String(item.identfid));
                    dataProvider.update('attendants', { id: item.id, data: { ...xmlAttendant, chainId }, previousData: item });
                });

                if (withConflicts.length) {
                    setConflictAttendants(withConflicts);
                    setShowConflictList(true);
                    setIsLoading(false);
                    if (withoutConflicts.length) importAttendantsApi(withoutConflicts);
                } else if (withoutConflicts.length) {
                    importAttendantsApi(withoutConflicts, handleClose);
                } else if (!withoutConflicts.length) {
                    if (withAutoUpdate.length) {
                        notify(`${withAutoUpdate.length} registro(s) atualizado(s) automaticamente`, { type: 'info' });
                    } else {
                        notify('Arquivo já foi importado', { type: 'info' });
                    }
                    handleClose();
                } else if (!data.length) {
                    notify('Arquivo está vazio', { type: 'info' });
                    handleClose();
                }
            })
            .catch(() => {
                notify('Não foi possível carregar o arquivo', { type: 'error' });
                setIsLoading(false);
            });
    };

    const importAttendantsApi = (data, onSuccessCallback) => {
        dataProvider.create('attendants', { data: data.map(item => ({ ...item, chainId })) })
            .then(({ data }) => {
                notify(`O total de ${data.attendants.length} registros foram importados`, { type: 'info' });
            })
            .catch((error) => {
                notify(error.message || 'Não foi possível realizar a importação do arquivo', { type: 'error' });
            })
            .finally(() => {
                setIsLoading(false);
                onSuccessCallback && onSuccessCallback();
            });
    };

    const handleImport = () => {
        if (!validation || validation.status !== 'valid') return;
        const { validRows } = validation;
        setXmlConflictAttendants(validRows);
        findAttendantsApi({ identfids: validRows.map(item => item.identfid) }, validRows);
    };

    const resolveAllConflicts = () => {
        const list = conflictAttendants.map(attendant => ({
            ...attendant,
            newAttendant: xmlConflictAttendants.find(x => x.identfid === attendant.identfid),
        }));
        list.forEach(record => {
            const { newAttendant, ...attendant } = record;
            dataProvider.update('attendants', { id: attendant.id, data: { ...newAttendant, chainId }, previousData: attendant });
        });
        notify(`${conflictAttendants.length} registro(s) substituído(s) pela planilha`, { type: 'info' });
        handleClose();
    };

    const dialogMaxWidth = showConflictList ? 1040 : 760;

    return (
        <Dialog
            fullWidth
            open={open}
            onClose={handleClose}
            aria-label="Importar frentistas"
            maxWidth={false}
            slotProps={{ paper: { sx: { width: '100%', maxWidth: dialogMaxWidth, m: 2 } } }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', p: 3, pb: 1 }}>
                <Box>
                    <Typography variant="h6" fontWeight={700}>
                        Importar frentistas
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Cadastre vários frentistas de uma vez por planilha
                    </Typography>
                </Box>
                <IconButton size="small" onClick={handleClose} aria-label="Fechar" disabled={isLoading}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent sx={{ pt: 1 }}>
                {showConflictList ? (
                    <ConflictView
                        conflictAttendants={conflictAttendants}
                        xmlConflictAttendants={xmlConflictAttendants}
                        chainId={chainId}
                        onResolveAll={resolveAllConflicts}
                        postCommitCallback={handleClose}
                    />
                ) : (
                    <Box>
                        <TemplateCard />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 3, mb: 1.5 }}>
                            <Typography variant="body1" fontWeight={700}>
                                Sua planilha precisa ter estas colunas
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                A ordem não importa
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {REQUIRED_COLUMNS.map(col => (
                                <ColumnChip
                                    key={col.key}
                                    name={col.label}
                                    description={col.description}
                                    error={validation?.missingColumns?.includes(col.key)}
                                    valid={validation?.status === 'valid'}
                                />
                            ))}
                        </Stack>

                        <Permission permission={PERMISSIONS.ADMIN}>
                            <ChainSelect value={chainId} onChange={setChainId} />
                        </Permission>

                        <FileDropZone
                            file={selectedFile}
                            onFile={onSelectFile}
                            status={validation?.status}
                            disabled={isLoading}
                        />

                        {validation?.status === 'validating' && (
                            <Box display="flex" alignItems="center" gap={2} mt={2}>
                                <CircularProgress size={18} />
                                <Typography variant="body2" color="text.secondary">
                                    Validando o arquivo...
                                </Typography>
                            </Box>
                        )}

                        {validation?.missingColumns?.length > 0 && (
                            <Alert
                                severity="error"
                                icon={<ErrorOutlineIcon fontSize="inherit" />}
                                sx={{ mt: 2 }}
                            >
                                <AlertTitle>
                                    {validation.missingColumns.length === 1 ? (
                                        <>
                                            Faltou a coluna{' '}
                                            <Box component="span" sx={{ fontFamily: 'monospace' }}>
                                                {columnLabel(validation.missingColumns[0])}
                                            </Box>
                                        </>
                                    ) : (
                                        <>
                                            Faltaram colunas:{' '}
                                            {validation.missingColumns.map((k, i) => (
                                                <React.Fragment key={k}>
                                                    {i > 0 && ', '}
                                                    <Box component="span" sx={{ fontFamily: 'monospace' }}>
                                                        {columnLabel(k)}
                                                    </Box>
                                                </React.Fragment>
                                            ))}
                                        </>
                                    )}
                                </AlertTitle>
                                {validation.missingColumns.length === 1
                                    ? 'Adicione essa coluna na planilha (ou use nosso modelo) e tente de novo.'
                                    : 'Adicione essas colunas na planilha (ou use nosso modelo) e tente de novo.'}
                            </Alert>
                        )}

                        {validation?.fileError && (
                            <Alert severity="error" sx={{ mt: 2, '& .MuiAlert-message': { whiteSpace: 'pre-line' } }}>
                                <AlertTitle>Não foi possível importar</AlertTitle>
                                {validation.fileError}
                            </Alert>
                        )}

                        {validation?.rowErrors?.length > 0 && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                <AlertTitle>{validation.rowErrors.length} linha(s) ignorada(s)</AlertTitle>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    As linhas abaixo foram ignoradas porque têm dados faltando ou inválidos. Corrija e reenvie se necessário.
                                </Typography>
                                <Box sx={{ maxHeight: 240, overflowY: 'auto' }}>
                                    <MuiList dense disablePadding>
                                        {validation.rowErrors.slice(0, 50).map((err, idx) => (
                                            <ListItem key={idx} sx={{ py: 0.25 }}>
                                                <ListItemText
                                                    primary={`Linha ${err.line}: ${err.reasons.join('; ')}`}
                                                    secondary={`cpf: ${err.cpf || '—'} | nome: ${err.name || '—'} | identificação: ${err.identfid || '—'}`}
                                                />
                                            </ListItem>
                                        ))}
                                        {validation.rowErrors.length > 50 && (
                                            <ListItem sx={{ py: 0.25 }}>
                                                <ListItemText primary={`... e mais ${validation.rowErrors.length - 50} linha(s).`} />
                                            </ListItem>
                                        )}
                                    </MuiList>
                                </Box>
                            </Alert>
                        )}

                        {validation?.status === 'valid' && (
                            <Alert
                                severity="success"
                                icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                                sx={{ mt: 2 }}
                            >
                                <AlertTitle>
                                    Tudo certo — {validation.validRows.length} frentista{validation.validRows.length === 1 ? '' : 's'} prontos para importar
                                </AlertTitle>
                                {validation.rowErrors.length > 0
                                    ? `${validation.rowErrors.length} linha(s) serão ignoradas — veja o aviso acima.`
                                    : 'Clique em IMPORTAR para concluir.'}
                            </Alert>
                        )}

                        {isLoading && (
                            <Box display="flex" alignItems="center" gap={2} mt={2}>
                                <CircularProgress size={18} />
                                <Typography variant="body2" color="text.secondary">
                                    Importando registros...
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>

            {!showConflictList && (
                <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                        {validation?.status === 'valid'
                            ? `${validation.validRows.length} linha${validation.validRows.length === 1 ? '' : 's'} válida${validation.validRows.length === 1 ? '' : 's'}`
                            : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={handleClose} label="Cancelar" color="success" disabled={isLoading} />
                        <Button
                            onClick={handleImport}
                            label="Importar"
                            variant="contained"
                            color="success"
                            disableElevation
                            disabled={validation?.status !== 'valid' || isLoading}
                            startIcon={isLoading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <CloudUploadIcon />}
                            sx={{ px: 2.5, py: 1, fontWeight: 700 }}
                        />
                    </Box>
                </DialogActions>
            )}
        </Dialog>
    );
};

const ImportCSVButton = () => {
    const [showDialog, setShowDialog] = useState(false);

    return (
        <Fragment>
            <Button onClick={() => setShowDialog(true)} label="Importar planilha">
                <IconContentAdd style={{ transform: 'rotate(180deg)', fontSize: '20' }} />
            </Button>
            {showDialog && (
                <ImportDialog open={showDialog} onClose={() => setShowDialog(false)} />
            )}
        </Fragment>
    );
};

export default ImportCSVButton;
